import type { XYPosition } from '@xyflow/react';

/**
 * Takes source and target {x, y} points, together with an array of number
 * tuples [x, y] representing the points along the path, and returns a string
 * to be used as the SVG path.
 */
export type SVGDrawFunction = (source: XYPosition, target: XYPosition, path: number[][]) => string;

/**
 * Draws a SVG path from a list of points, using straight lines.
 */
export const svgDrawStraightLinePath: SVGDrawFunction = (source, target, path) => {
	let svgPathString = `M ${source.x}, ${source.y} `;

	path.forEach((point) => {
		const [x, y] = point;
		svgPathString += `L ${x}, ${y} `;
	});

	svgPathString += `L ${target.x}, ${target.y} `;

	return svgPathString;
};

/**
 * Draws a SVG path from a list of points, using rounded lines.
 */
export const svgDrawSmoothLinePath: SVGDrawFunction = (source, target, path) => {
	const points = [[source.x, source.y], ...path, [target.x, target.y]];
	return quadraticBezierCurve(points);
};

const quadraticBezierCurve = (points: number[][]) => {
	const X = 0;
	const Y = 1;
	let point = points[0];

	const first = points[0];
	let svgPath = `M${first[X]},${first[Y]}M`;

	for (let i = 0; i < points.length; i++) {
		const next = points[i];
		const midPoint = getMidPoint(point[X], point[Y], next[X], next[Y]);

		svgPath += ` ${midPoint[X]},${midPoint[Y]}`;
		svgPath += `Q${next[X]},${next[Y]}`;
		point = next;
	}

	const last = points[points.length - 1];
	svgPath += ` ${last[0]},${last[1]}`;

	return svgPath;
};

const getMidPoint = (Ax: number, Ay: number, Bx: number, By: number) => {
	const Zx = (Ax - Bx) / 2 + Bx;
	const Zy = (Ay - By) / 2 + By;
	return [Zx, Zy];
};

export const rdpSimplify = (points: number[][], epsilon: number): number[][] => {
	if (points.length < 3) {
		return points;
	}

	const [start, end] = [points[0], points[points.length - 1]];
	let maxDist = 0;
	let index = 0;

	for (let i = 1; i < points.length - 1; i++) {
		const dist = perpendicularDistance(points[i], start, end);
		if (dist > maxDist) {
			maxDist = dist;
			index = i;
		}
	}

	if (maxDist > epsilon) {
		const left = rdpSimplify(points.slice(0, index + 1), epsilon);
		const right = rdpSimplify(points.slice(index), epsilon);
		return [...left.slice(0, -1), ...right];
	} else {
		return [start, end];
	}
};

const perpendicularDistance = (point: number[], lineStart: number[], lineEnd: number[]): number => {
	const [x0, y0] = point;
	const [x1, y1] = lineStart;
	const [x2, y2] = lineEnd;
	const num = Math.abs((y2 - y1) * x0 - (x2 - x1) * y0 + x2 * y1 - y2 * x1);
	const den = Math.sqrt((y2 - y1) ** 2 + (x2 - x1) ** 2);
	return num / den;
};
